import { REJECT, array, bool, nullable, num, object, oneOf, optional, str, type Received } from 'validation';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';
import { getRecord } from 'api/shared/getRecord';

import {
  TRIGGER_ACTIONS,
  TRIGGER_RESTART_FIRE_MODE_DEFAULT,
  TRIGGER_RESTART_FIRE_MODES,
  triggerParameterSchema,
  type TriggerAction,
  type TriggerParameter,
  type TriggerRestartFireMode,
} from './types';

// GET /triggers/:trigger_id — the single-record read (full detail). Backend:
// routes/triggers.js, which runs `SELECT t.*, z.name AS zone_name`, then attaches
// `folder_path` (buildFolderPath), the `status_change` sub-record, and the monitor
// zones (fetchMonitorZones).
//
// NESTED, unlike the flat list row in ./getTriggers.ts. This is the canonical
// single-record read — the bare `<Entity>` the other trigger shapes
// (`TriggerRow`, `CreatedTrigger`, `UpdatedTrigger`) are described relative to.
//
// `status_change` is redeclared in full here, and again in postTrigger /
// putTrigger. The three handlers each carry their own verbatim copy of the same
// `SELECT tnsc.*, …` statement — equal-by-coincidence, not identity, since the
// sub-object exemption in ../../README.md is for one shared *serializer*. Three
// copy-pasted queries are precisely the case the rule guards: a backend edit can
// update one and miss the others.

/**
 * The status-change detail of a trigger. Present only when the trigger's `type` is
 * `node_status_change` AND a `triggers_node_status_change` row exists — hence
 * `optional` on the owning field rather than `nullable`; the route omits the key,
 * it never sends null.
 *
 * Because the row is known to exist wherever this object appears, the table's
 * `NOT NULL` columns are genuinely non-null here (`action`, `restart_fire_mode`) —
 * the opposite of the flat list row, where the same two are nullable because the
 * join may not match. The `*_name` fields come from LEFT JOINs for display and
 * are nullable regardless.
 */
export interface TriggerStatusChange {
  readonly trigger_node_status_change_id: number;
  readonly trigger_id: number;
  readonly node_definition_id: number | null;
  readonly node_definition_name: string | null;
  readonly from_status: string | null;
  readonly to_status: string | null;
  readonly action: TriggerAction;
  readonly target_node_definition_id: number | null;
  readonly target_node_definition_name: string | null;
  // The target node definition's own default queue, echoed so a UI can say what
  // "leave the queue empty" will actually resolve to.
  readonly target_default_queue_id: number | null;
  readonly target_event_id: number | null;
  readonly target_event_name: string | null;
  readonly target_event_zone_name: string | null;
  readonly queue_id: number | null;
  readonly queue_name: string | null;
  readonly queue_zone_name: string | null;
  readonly parameters: readonly TriggerParameter[] | null;
  readonly restart_fire_mode: TriggerRestartFireMode;
  // IANA timezone the submit_child submit runs in; null falls back to the configured default, then UTC. The route stores
  // null for any other action.
  readonly timezone: string | null;
  // The status-change row's own timestamps, not the trigger's — and nullable for
  // the same reason as the trigger's: `DEFAULT CURRENT_TIMESTAMP` with no NOT NULL.
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

export interface Trigger {
  readonly trigger_id: number;
  // Nullable: `triggers.zone_id` carries no NOT NULL (migration 0180), and the SQL
  // LEFT JOINs zones, so `zone_name` is nullable for the same reason.
  readonly zone_id: number | null;
  readonly zone_name: string | null;
  readonly name: string;
  readonly type: string;
  readonly folder_id: number | null;
  readonly folder_path: FolderPath | null;
  readonly monitor_all_zones: boolean;
  // Always arrays (fetchMonitorZones maps its rows). Empty with
  // `monitor_all_zones` true is how "all zones, present and future" is encoded.
  readonly monitor_zone_ids: readonly number[];
  readonly monitor_zone_names: readonly string[];
  // See getTriggers for why these are nullable: the columns carry a default but no
  // NOT NULL, and `str()` would report a null as '' rather than as "unknown".
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly status_change?: TriggerStatusChange;
}

// `documentation` is deliberately NOT carried, though this read returns it: the
// SELECT is `t.*` and migration 0288 added the column to thirteen tables, whose UI
// #1047 owns as one piece. Dropping it from the type is safe HERE specifically —
// the PUT handler gates the column on `documentationProvided`
// (hasOwnProperty on the body), so a write body that omits the key preserves what
// is stored. That is not true everywhere: python_libraries' PUT overwrites it
// unconditionally, which is why its own note reads as a warning and this one does
// not.

const statusChangeSchema = object<TriggerStatusChange>({
  trigger_node_status_change_id: num(),
  trigger_id: num(),
  node_definition_id: nullable(num()),
  node_definition_name: nullable(str()),
  from_status: nullable(str()),
  to_status: nullable(str()),
  // Non-nullable, so unlike the list row the `oneOf` fallback really is what a
  // drifted value becomes. Both defaults match what the backend would itself have
  // written for an absent field.
  action: oneOf(TRIGGER_ACTIONS, 'raise_event'),
  target_node_definition_id: nullable(num()),
  target_node_definition_name: nullable(str()),
  target_default_queue_id: nullable(num()),
  target_event_id: nullable(num()),
  target_event_name: nullable(str()),
  target_event_zone_name: nullable(str()),
  queue_id: nullable(num()),
  queue_name: nullable(str()),
  queue_zone_name: nullable(str()),
  parameters: nullable(array(triggerParameterSchema)),
  restart_fire_mode: oneOf(TRIGGER_RESTART_FIRE_MODES, TRIGGER_RESTART_FIRE_MODE_DEFAULT),
  timezone: nullable(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
});

const triggerSchema = object<Trigger>({
  trigger_id: num(REJECT),
  zone_id: nullable(num()),
  zone_name: nullable(str()),
  name: str(),
  type: str(),
  folder_id: nullable(num()),
  folder_path: nullable(folderPathSchema),
  monitor_all_zones: bool(),
  monitor_zone_ids: array(num()),
  monitor_zone_names: array(str()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  status_change: optional(statusChangeSchema),
});

/**
 * GET `/triggers/:trigger_id` — one record for the edit dialog, so it never opens
 * on a loosely cast store row (and the flat list row could not seed it anyway: it
 * is a different shape).
 *
 * Boundary-validated. See `getRecord` for what each outcome means; the short
 * version is that `null` means the server said there is no such trigger, and
 * `degraded` / `unreadable` mean it said something we could not read in full.
 *
 * A caller must open a form on `ok` alone. The trigger dialog chooses create vs
 * update from how it was opened, not from whether it holds a record, so a bad
 * answer does not duplicate anything — it does something quieter and worse:
 * saving PUTs whatever the form holds over a trigger that was configured
 * correctly. On `unreadable` that is the form's empty initial state; on
 * `degraded` it is our substitutes. `recordToEdit` refuses both.
 *
 * Only `trigger_id` carries `REJECT`, because it is the only field with no
 * substitute for ANY consumer. A drifted `zone_id` — which POST/PUT are keyed on —
 * degrades rather than fails, and this record's one consumer refuses that too.
 *
 * WHY THE CONSUMER REFUSES EVERYTHING, rather than a list of fields that matter:
 * every parser here substitutes on drift instead of failing, `SaveTrigger` sends
 * back nearly every field, and the backend accepts most substitutes. A null
 * watch-node, from/to status or queue is a WILDCARD to the trigger engine, so it
 * broadens what fires; a dropped parameter element loses its override; a null
 * `parameters` saves as `[]`. A list of the fields that matter would have to be
 * right about each of these and about every field added later, so there is none:
 * any degradation is refused. The general problem — a client that cannot represent
 * what a newer server sent, and destroys it on save — is #1091.
 *
 * `status_change` is `optional`, and ABSENT AND MALFORMED DO NOT READ THE SAME. A
 * genuinely missing key (the route omits it for other trigger types) parses
 * silently; a malformed one also recovers to `undefined`, but with a warning, so
 * the answer is `degraded` rather than "this trigger has no status-change detail".
 * That falls out of the parse — it needs no guard here.
 */
export const getTrigger = (id: number): Promise<Received<Trigger | null>> => getRecord(`/triggers/${id}`, { schema: triggerSchema, source: 'getTrigger' });
